import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Palette as ThemeIcon,
  Language as LanguageIcon,
  Storage as DataIcon,
  Api as ApiIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  Download as ExportIcon,
  Upload as ImportIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';

interface UserProfile {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  department: string;
  avatar: string;
  timezone: string;
  language: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  projectUpdates: boolean;
  teamMessages: boolean;
  systemAlerts: boolean;
  weeklyReports: boolean;
  dailyDigest: boolean;
}

interface SecuritySettings {
  twoFactorAuth: boolean;
  sessionTimeout: number;
  passwordExpiry: number;
  loginAttempts: number;
  ipWhitelist: string[];
}

interface SystemSettings {
  theme: 'light' | 'dark' | 'auto';
  defaultProject: string;
  dashboardLayout: 'grid' | 'list';
  autoSave: boolean;
  dataRetention: number;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Settings Component for Project Manager
 * Manages user profile, notifications, security, and system preferences
 */
const Settings: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [notifications, setNotifications] = useState<NotificationSettings | null>(null);
  const [security, setSecurity] = useState<SecuritySettings | null>(null);
  const [system, setSystem] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const projects = ['E-commerce Platform Redesign', 'Mobile App Development', 'Dashboard Analytics Implementation'];
  const timezones = ['UTC-8 (PST)', 'UTC-5 (EST)', 'UTC+0 (GMT)', 'UTC+1 (CET)', 'UTC+8 (CST)'];
  const languages = ['English', 'Spanish', 'French', 'German', 'Chinese'];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Mock data for development - replace with actual API calls
      const mockProfile: UserProfile = {
        id: 1,
        firstName: "Sarah",
        lastName: "Johnson",
        email: "sarah.johnson@company.com",
        phone: "+1 (555) 123-4567",
        role: "Project Manager",
        department: "Engineering",
        avatar: "/avatars/sarah.jpg",
        timezone: "UTC-8 (PST)",
        language: "English"
      };

      const mockNotifications: NotificationSettings = {
        emailNotifications: true,
        pushNotifications: true,
        smsNotifications: false,
        projectUpdates: true,
        teamMessages: true,
        systemAlerts: true,
        weeklyReports: true,
        dailyDigest: false
      };

      const mockSecurity: SecuritySettings = {
        twoFactorAuth: true,
        sessionTimeout: 30,
        passwordExpiry: 90,
        loginAttempts: 5,
        ipWhitelist: ['192.168.1.0/24', '10.0.0.0/8']
      };

      const mockSystem: SystemSettings = {
        theme: 'light',
        defaultProject: 'E-commerce Platform Redesign',
        dashboardLayout: 'grid',
        autoSave: true,
        dataRetention: 365,
        backupFrequency: 'weekly'
      };
      
      setProfile(mockProfile);
      setNotifications(mockNotifications);
      setSecurity(mockSecurity);
      setSystem(mockSystem);
    } catch (err) {
      setError('Failed to load settings. Please try again.');
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Here you would make actual API calls to save settings
      console.log('Saving settings:', { profile, notifications, security, system });
      
      // Show success message
      setError(null);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = () => {
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    // Here you would make API call to change password
    setPasswordDialog(false);
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  const handleNotificationChange = (key: keyof NotificationSettings) => {
    if (!notifications) return;
    setNotifications({
      ...notifications,
      [key]: !notifications[key]
    });
  };

  const handleSecurityChange = (key: keyof SecuritySettings, value: any) => {
    if (!security) return;
    setSecurity({
      ...security,
      [key]: value
    });
  };

  const handleSystemChange = (key: keyof SystemSettings, value: any) => {
    if (!system) return;
    setSystem({
      ...system,
      [key]: value
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Settings
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={loadSettings}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="settings tabs">
            <Tab icon={<PersonIcon />} label="Profile" />
            <Tab icon={<NotificationsIcon />} label="Notifications" />
            <Tab icon={<SecurityIcon />} label="Security" />
            <Tab icon={<SettingsIcon />} label="System" />
          </Tabs>
        </Box>

        {/* Profile Tab */}
        <TabPanel value={activeTab} index={0}>
          {profile && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              
              <Box display="flex" alignItems="center" gap={3} sx={{ mb: 4 }}>
                <Avatar 
                  sx={{ width: 80, height: 80 }}
                  src={profile.avatar}
                >
                  {profile.firstName[0]}{profile.lastName[0]}
                </Avatar>
                <Box>
                  <Button variant="outlined" size="small" sx={{ mr: 1 }}>
                    Change Photo
                  </Button>
                  <Button variant="text" size="small" color="error">
                    Remove
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)'
                  },
                  gap: 3,
                  mb: 4
                }}
              >
                <TextField
                  label="First Name"
                  value={profile.firstName}
                  onChange={(e) => setProfile({...profile, firstName: e.target.value})}
                  fullWidth
                />
                <TextField
                  label="Last Name"
                  value={profile.lastName}
                  onChange={(e) => setProfile({...profile, lastName: e.target.value})}
                  fullWidth
                />
                <TextField
                  label="Email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({...profile, email: e.target.value})}
                  fullWidth
                />
                <TextField
                  label="Phone"
                  value={profile.phone}
                  onChange={(e) => setProfile({...profile, phone: e.target.value})}
                  fullWidth
                />
                <TextField
                  label="Role"
                  value={profile.role}
                  disabled
                  fullWidth
                />
                <TextField
                  label="Department"
                  value={profile.department}
                  disabled
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={profile.timezone}
                    label="Timezone"
                    onChange={(e) => setProfile({...profile, timezone: e.target.value})}
                  >
                    {timezones.map(tz => (
                      <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={profile.language}
                    label="Language"
                    onChange={(e) => setProfile({...profile, language: e.target.value})}
                  >
                    {languages.map(lang => (
                      <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Security
              </Typography>
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setPasswordDialog(true)}
              >
                Change Password
              </Button>
            </Box>
          )}
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={activeTab} index={1}>
          {notifications && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Notification Preferences
              </Typography>
              
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Communication Methods</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Email Notifications"
                        secondary="Receive notifications via email"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.emailNotifications}
                          onChange={() => handleNotificationChange('emailNotifications')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Push Notifications"
                        secondary="Browser and mobile push notifications"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.pushNotifications}
                          onChange={() => handleNotificationChange('pushNotifications')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="SMS Notifications"
                        secondary="Text message notifications for urgent items"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.smsNotifications}
                          onChange={() => handleNotificationChange('smsNotifications')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Content Types</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Project Updates"
                        secondary="Status changes, milestones, and deadlines"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.projectUpdates}
                          onChange={() => handleNotificationChange('projectUpdates')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Team Messages"
                        secondary="Messages and communications from team members"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.teamMessages}
                          onChange={() => handleNotificationChange('teamMessages')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="System Alerts"
                        secondary="System maintenance and security alerts"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.systemAlerts}
                          onChange={() => handleNotificationChange('systemAlerts')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Weekly Reports"
                        secondary="Automated weekly project reports"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.weeklyReports}
                          onChange={() => handleNotificationChange('weeklyReports')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Daily Digest"
                        secondary="Daily summary of activities and updates"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.dailyDigest}
                          onChange={() => handleNotificationChange('dailyDigest')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={activeTab} index={2}>
          {security && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Security Settings
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemText 
                    primary="Two-Factor Authentication"
                    secondary="Add an extra layer of security to your account"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={security.twoFactorAuth}
                      onChange={() => handleSecurityChange('twoFactorAuth', !security.twoFactorAuth)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText 
                    primary="Session Timeout"
                    secondary="Auto-logout after inactivity (minutes)"
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      value={security.sessionTimeout}
                      onChange={(e) => handleSecurityChange('sessionTimeout', parseInt(e.target.value))}
                      size="small"
                      sx={{ width: 80 }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText 
                    primary="Password Expiry"
                    secondary="Force password change every X days"
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      value={security.passwordExpiry}
                      onChange={(e) => handleSecurityChange('passwordExpiry', parseInt(e.target.value))}
                      size="small"
                      sx={{ width: 80 }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText 
                    primary="Login Attempts"
                    secondary="Maximum failed login attempts before lockout"
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      value={security.loginAttempts}
                      onChange={(e) => handleSecurityChange('loginAttempts', parseInt(e.target.value))}
                      size="small"
                      sx={{ width: 80 }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  IP Whitelist
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Restrict access to specific IP addresses or ranges
                </Typography>
                {security.ipWhitelist.map((ip, index) => (
                  <Chip
                    key={index}
                    label={ip}
                    onDelete={() => {
                      const newList = security.ipWhitelist.filter((_, i) => i !== index);
                      handleSecurityChange('ipWhitelist', newList);
                    }}
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
                <Button variant="outlined" size="small" startIcon={<AddIcon />}>
                  Add IP Range
                </Button>
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* System Tab */}
        <TabPanel value={activeTab} index={3}>
          {system && (
            <Box>
              <Typography variant="h6" gutterBottom>
                System Preferences
              </Typography>
              
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)'
                  },
                  gap: 3,
                  mb: 4
                }}
              >
                <FormControl fullWidth>
                  <InputLabel>Theme</InputLabel>
                  <Select
                    value={system.theme}
                    label="Theme"
                    onChange={(e) => handleSystemChange('theme', e.target.value)}
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                    <MenuItem value="auto">Auto</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Default Project</InputLabel>
                  <Select
                    value={system.defaultProject}
                    label="Default Project"
                    onChange={(e) => handleSystemChange('defaultProject', e.target.value)}
                  >
                    {projects.map(project => (
                      <MenuItem key={project} value={project}>{project}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Dashboard Layout</InputLabel>
                  <Select
                    value={system.dashboardLayout}
                    label="Dashboard Layout"
                    onChange={(e) => handleSystemChange('dashboardLayout', e.target.value)}
                  >
                    <MenuItem value="grid">Grid</MenuItem>
                    <MenuItem value="list">List</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Backup Frequency</InputLabel>
                  <Select
                    value={system.backupFrequency}
                    label="Backup Frequency"
                    onChange={(e) => handleSystemChange('backupFrequency', e.target.value)}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <List>
                <ListItem>
                  <ListItemText 
                    primary="Auto-save"
                    secondary="Automatically save changes as you work"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={system.autoSave}
                      onChange={() => handleSystemChange('autoSave', !system.autoSave)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText 
                    primary="Data Retention"
                    secondary="Keep data for X days before archiving"
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      value={system.dataRetention}
                      onChange={(e) => handleSystemChange('dataRetention', parseInt(e.target.value))}
                      size="small"
                      sx={{ width: 100 }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Data Management
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button variant="outlined" startIcon={<ExportIcon />}>
                  Export Data
                </Button>
                <Button variant="outlined" startIcon={<ImportIcon />}>
                  Import Data
                </Button>
                <Button variant="outlined" color="error" startIcon={<DeleteIcon />}>
                  Delete Account
                </Button>
              </Box>
            </Box>
          )}
        </TabPanel>
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1, minWidth: 300 }}>
            <TextField
              label="Current Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
            />
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
            />
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePasswordChange}>
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;